import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import type { Lucid } from '../../../types/types';
import Link from '../../link/link';
import Text from '../../text/text';
import Card, { type CardElementProps } from '../../card/card';
import Box from '../../box/box';
import Image from '../../image/image';

export interface EduCardProps extends Omit<CardElementProps, 'divider'> {
  /** List of features to be displayed on card */
  featureList?: Lucid.Directus.Repeaters.Edu_Account_Type_Feature_List[];
}

const EduCard: FC<PropsWithChildren<EduCardProps>> = ({ heading, link, media, featureList }) => (
  <Box
    css={theme => ({
      label: 'eduCard',
      minHeight: '100%',
      marginBottom: theme.space.s40,
      [theme.breakpoints.bp768.min]: {
        flex: '0 1 30%',
        marginBottom: theme.space.s0,
      },
    })}
  >
    <Card
      css={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '0 1 100%',
        cursor: 'inherit',
      }}
    >
      <Box
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.space.s24,
        })}
      >
        <Image
          css={theme => ({
            width: '60px',
            marginBottom: theme.space.s16,
          })}
          imageAlt=""
          imageSrc={withImage(media?.image)}
        />
        <Card.Heading as="h3" css={{ textAlign: 'center' }} heading={heading} />
      </Box>
      <Card.Divider />
      <Box
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          flex: 'auto',
          justifyContent: 'space-between',
          padding: theme.space.s32,
        })}
      >
        <ul
          css={theme => ({
            marginBottom: theme.space.s32,
            marginLeft: 0,
            listStyle: 'none',
          })}
        >
          {featureList?.map((feature, index) => (
            <li key={`${kebabCase(feature.text)}-${index + 1}`}>
              <Text variant="interactiveS500">{feature.text}</Text>
            </li>
          ))}
        </ul>
        {link?.final_url && (
          <Box
            css={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
            data-testid="edu-card-link-container"
          >
            <Link displayAsButton css={{ margin: 0 }} href={link.final_url}>
              {link?.default_link_text}
            </Link>
          </Box>
        )}
      </Box>
    </Card>
  </Box>
);

export default EduCard;
